import { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, type SortingState } from '@tanstack/react-table';
import { X, Download } from 'lucide-react';
import type { NlqResult } from '../engine/NlqEngine';
interface Props {
  result: NlqResult;
  onClose: () => void;
}
export function ResultsOverlay({
  result,
  onClose
}: Props) {
  const rows = result.results?.rows ?? [];
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    const allKeys = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        allKeys.add(key);
      }
    }
    return Array.from(allKeys).map(key => ({
      accessorKey: key,
      header: key,
      cell: (info: {
        getValue: () => unknown;
      }) => {
        const val = info.getValue();
        if (val === null || val === undefined) return '-';
        if (typeof val === 'number') return val.toLocaleString();
        return String(val);
      }
    }));
  }, [rows]);
  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });
  const handleExport = (scope: 'selected' | 'page' | 'all') => {
    let dataToExport: Record<string, unknown>[];
    if (scope === 'page') {
      dataToExport = table.getRowModel().rows.map(r => r.original);
    } else {
      dataToExport = rows;
    }
    if (dataToExport.length === 0) return;
    const headers = Object.keys(dataToExport[0]);
    const csvRows = [headers.join(','), ...dataToExport.map(row => headers.map(h => {
      const val = row[h];
      const str = val === null || val === undefined ? '' : String(val);
      return str.includes(',') ? `"${str}"` : str;
    }).join(','))];
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ondemand-results-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className="nlq-overlay" onClick={onClose} data-id="element-1322" data-mp="lujal1wl36uh">
      <div className="nlq-overlay-content" onClick={e => e.stopPropagation()} data-id="element-1323" data-mp="1ij7fcwjagxly">
        <div className="nlq-overlay-header" data-id="element-1324" data-mp="1ehm7jplm39im">
          <h3 data-id="element-1325" data-mp="1ehvypgie7mpw">
            Results ({result.results?.metadata.totalRows ?? 0} rows)
          </h3>
          <div className="nlq-overlay-actions" data-id="element-1326" data-mp="19p2tysmxcedt">
            <button onClick={() => handleExport('page')} className="nlq-export-btn" data-id="element-1327" data-mp="14rsvyolgyued">
              <Download size={14} data-id="element-1328" data-mp="14pq5k3gemz94" /> Export page
            </button>
            <button onClick={() => handleExport('all')} className="nlq-export-btn" data-id="element-1329" data-mp="228htw77atcih">
              <Download size={14} data-id="element-1330" data-mp="1xfgd1tnj72w9" /> Export all
            </button>
            <button onClick={onClose} className="nlq-close-btn" data-id="element-1331" data-mp="24rwdk07rmro2">
              <X size={16} data-id="element-1332" data-mp="24thb7ypdmqq5" />
            </button>
          </div>
        </div>

        {result.explanation && <div className="nlq-explanation" data-id="element-1333" data-mp="2epk9gth3y57t"><ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-1334" data-mp="2epasfve1j8e9">{result.explanation}</ReactMarkdown></div>}

        <div className="nlq-table-wrapper" data-id="element-1335" data-mp="2is75l6ktacat">
          <table className="nlq-table" data-id="element-1336" data-mp="2ip1ghrc6naf3">
            <thead data-id="element-1337" data-mp="2nqqpz2jrbg12">
              {table.getHeaderGroups().map(headerGroup => <tr key={headerGroup.id} data-id="element-1338" data-mp="2sp24hqkzyepm">
                  {headerGroup.headers.map(header => <th key={header.id} onClick={header.column.getToggleSortingHandler()} className="nlq-th" data-id="element-1339" data-mp="2xgaeoou5i43u">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                  asc: ' ↑',
                  desc: ' ↓'
                }[header.column.getIsSorted() as string] ?? ''}
                    </th>)}
                </tr>)}
            </thead>
            <tbody data-id="element-1340" data-mp="2tqzgeom8pwz9">
              {table.getRowModel().rows.map(row => <tr key={row.id} className="nlq-tr" data-id="element-1341" data-mp="37humjt5ctzz5">
                  {row.getVisibleCells().map(cell => <td key={cell.id} className="nlq-td" data-id="element-1342" data-mp="32kcl0nto2n16">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}